/**
 * Monetization / ads / data-foundation vocabularies (Wave 1). The single source
 * of truth for the feature-flag keys + their DEFAULTS, event-type names, and the
 * economy / promo / cosmetic / tournament enums. Every enum here MIRRORS a Prisma
 * enum in `packages/db/prisma/schema.prisma` — keep them in lockstep.
 *
 * Nothing here is user-identifying; these are pure vocabularies shared by the
 * api, game-server, bot, and (later) the web/admin frontends.
 */

// ---------------------------------------------------------------------------
// Feature flags
// ---------------------------------------------------------------------------

/**
 * The central kill-switches. Stored as `FeatureFlag` rows keyed by these strings
 * (the DB column is an open `String`, so adding a flag needs NO migration — just
 * a new key here). Read live with a short TTL cache (`FlagService` ≤5s; the
 * game-server reader ~10s), seeded create-if-missing on api boot.
 *
 * TWO classes (see {@link FLAG_DEFAULTS}): `data_collection` is ON (server-side,
 * privacy-respecting, internal-id only, never surfaced) — every monetization /
 * ads / economy / promo user-facing flag is OFF until deliberately enabled.
 */
export const FLAG_KEYS = [
  "data_collection",
  "rating_ui",
  "economy",
  "payments",
  "promos",
  "tournaments",
  "cosmetics",
  "battlepass",
  "vip",
  "referral_rewards",
] as const;
export type FlagKey = (typeof FLAG_KEYS)[number];

export function isFlagKey(value: string): value is FlagKey {
  return (FLAG_KEYS as readonly string[]).includes(value);
}

/**
 * Boot-seed defaults + the fail-safe fallback when a row is missing/unreadable.
 * `data_collection` ON; EVERY monetization/promo/economy flag OFF. The api's
 * idempotent seed only ever CREATES missing rows, so an admin's live toggle is
 * never overwritten (mirrors `DEFAULT_GAME_STATUSES`).
 */
export const FLAG_DEFAULTS: Record<FlagKey, boolean> = {
  data_collection: true,
  rating_ui: false,
  economy: false,
  payments: false,
  promos: false,
  tournaments: false,
  cosmetics: false,
  battlepass: false,
  vip: false,
  referral_rewards: false,
};

/** The one flag that gates all event emission + rollups (defaults ON). */
export const DATA_COLLECTION_FLAG: FlagKey = "data_collection";

// ---------------------------------------------------------------------------
// Events (append-only analytics signal)
// ---------------------------------------------------------------------------

/**
 * Known `Event.type` names. The DB column is an open `String` (append a new type
 * here with no migration); these constants keep call-sites typo-free. Emission
 * is fire-and-forget + gated by `data_collection`, only from already-async
 * points (match-end, presence, social) — NEVER the gameplay move loop. Payloads
 * must never carry a Telegram id (only the internal cuid `userId`).
 */
export const EVENT_TYPES = [
  // Funnel / engagement
  "app_open",
  "lobby_view",
  "session_start",
  "session_end",
  // Identity
  "user_created",
  // Gameplay
  "match_start",
  "match_end",
  "match_abandon",
  // Social / growth
  "friend_request",
  "friend_accept",
  "user_blocked",
  "invite_sent",
  "invite_accepted",
  "referral_captured",
  // Skill
  "rating_updated",
  // Monetization signals
  "promo_impression",
  "promo_click",
  "wallet_credit",
  "wallet_debit",
  "purchase_completed",
] as const;
/** `Event.type` is an OPEN string; {@link EVENT_TYPES} are the known values. */
export type EventType = (typeof EVENT_TYPES)[number] | (string & {});

export function isKnownEventType(value: string): boolean {
  return (EVENT_TYPES as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Economy (mirrors Prisma LedgerReason / PaymentProvider / PaymentStatus)
// ---------------------------------------------------------------------------

/** Why a token ledger entry was written. Append-only. */
export const LEDGER_REASONS = [
  "SIGNUP_BONUS",
  "DAILY_REWARD",
  "REFERRAL_REWARD",
  "PURCHASE",
  "TOURNAMENT_ENTRY",
  "TOURNAMENT_PRIZE",
  "COSMETIC_PURCHASE",
  "GIFT_SENT",
  "GIFT_RECEIVED",
  "ADMIN_ADJUSTMENT",
] as const;
export type LedgerReason = (typeof LEDGER_REASONS)[number];

/** Payment rails (adapters are stubs this pass). Append-only. */
export const PAYMENT_PROVIDERS = ["STARS", "CARD_TO_CARD", "CRYPTO"] as const;
export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];

/** Payment-intent lifecycle. Append-only. */
export const PAYMENT_STATUSES = ["PENDING", "PAID", "FAILED", "CANCELED", "REFUNDED"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

// ---------------------------------------------------------------------------
// Promos / campaigns (mirrors Prisma PromoSlot)
// ---------------------------------------------------------------------------

/** Where a managed promo can render (the plan's slot inventory). Append-only. */
export const PROMO_SLOTS = [
  "LOBBY_HERO",
  "SECONDARY_STRIP",
  "POST_GAME",
  "SPLASH",
  "INBOX",
  "BOT_PV",
  "COMING_SOON_TILE",
  "EMPTY_STATE",
] as const;
export type PromoSlot = (typeof PROMO_SLOTS)[number];

export function isPromoSlot(value: string): value is PromoSlot {
  return (PROMO_SLOTS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Cosmetics (mirrors Prisma CosmeticType)
// ---------------------------------------------------------------------------

/** Pure-cosmetic categories (no pay-to-win). Append-only. */
export const COSMETIC_TYPES = ["CARD_BACK", "FELT", "AVATAR_FRAME", "NAME_COLOR", "EMOTE"] as const;
export type CosmeticType = (typeof COSMETIC_TYPES)[number];

// ---------------------------------------------------------------------------
// Tournaments (mirrors Prisma TournamentFormat / TournamentStatus)
// ---------------------------------------------------------------------------

/** Tournament shapes. `TWO_PLUS_TWO` = the "2 users + 2 bots" table. Append-only. */
export const TOURNAMENT_FORMATS = ["BRACKET", "TWO_PLUS_TWO", "LEADERBOARD"] as const;
export type TournamentFormat = (typeof TOURNAMENT_FORMATS)[number];

/** Tournament lifecycle. Append-only. */
export const TOURNAMENT_STATUSES = [
  "DRAFT",
  "SCHEDULED",
  "REGISTERING",
  "ACTIVE",
  "COMPLETED",
  "CANCELED",
] as const;
export type TournamentStatus = (typeof TOURNAMENT_STATUSES)[number];

/** The starting Elo-like rating a fresh (user, game) pair gets. */
export const DEFAULT_RATING = 1200;
